import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { Card, CardHeader, CardTitle, CardContent, CardDescription, Button, Input, Badge } from '../components/ui';
import {
  DollarSign, TrendingUp, Plus, Trash2, Edit2, Save, X,
  Calendar, ChevronLeft, ChevronRight, Sparkles, BarChart3,
  PieChart, Target, Zap, ArrowLeft, Check, RefreshCw,
  ToggleLeft, ToggleRight, Tag,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { SkeletonCustomAccounts } from '../components/ui/Skeleton';
import { formatCurrency, formatNumber } from '../lib/utils';

// =============================================================================
// PRO UPSELL
// =============================================================================
const ProUpsell = () => (
  <div className="min-h-[60vh] flex items-center justify-center p-8">
    <div className="max-w-2xl text-center space-y-8">
      <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 mb-4">
        <Sparkles className="h-10 w-10 text-primary" />
      </div>
      <div>
        <h1 className="text-3xl font-bold mb-3">Custom Tracking Accounts</h1>
        <p className="text-lg text-muted-foreground max-w-lg mx-auto">
          Track ad spend and revenue from any source — affiliate networks, direct deals,
          custom campaigns, or platforms we don't integrate with yet.
        </p>
      </div>
      <div className="grid md:grid-cols-2 gap-6 text-left">
        {[
          { Icon: DollarSign, color: 'bg-red-500/10 text-red-500', title: 'Custom Ad Account', desc: 'Track spend from TikTok Ads, LinkedIn, Reddit, native ads, or direct buys.' },
          { Icon: TrendingUp, color: 'bg-green-500/10 text-green-500', title: 'Custom Revenue Account', desc: 'Track revenue from affiliate programs, sponsorships, direct ad sales, or any custom source.' },
        ].map(({ Icon, color, title, desc }) => (
          <Card key={title} className="border-2 border-dashed">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-lg ${color}`}><Icon className="h-6 w-6" /></div>
                <div><h3 className="font-semibold mb-1">{title}</h3><p className="text-sm text-muted-foreground">{desc}</p></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      <div className="flex flex-wrap justify-center gap-4 text-sm text-muted-foreground">
        {[BarChart3, PieChart, Target].map((Icon, i) => (
          <div key={i} className="flex items-center gap-2"><Icon className="h-4 w-4" /><span>{['Overview Integration','ROI Calculations','Custom Naming'][i]}</span></div>
        ))}
      </div>
      <div className="pt-4">
        <Button size="lg" className="gap-2" asChild>
          <a href="/subscribe"><Zap className="h-5 w-5" />Upgrade to Pro</a>
        </Button>
        <p className="text-sm text-muted-foreground mt-3">Pro users can create 1 custom ad account + 1 custom revenue account</p>
      </div>
    </div>
  </div>
);

// =============================================================================
// ACCOUNT CREATE / EDIT MODAL (small form — stays as modal, it's just a few fields)
// =============================================================================
const AccountModal = ({ type, account, onSave, onClose }) => {
  const isAd = type === 'ad';
  const [form, setForm] = useState({
    name: account?.name || '',
    description: account?.description || '',
    currency: account?.currency || 'USD',
    exchange_rate: account?.exchange_rate || 1,
    tax_rate: account?.tax_rate || 0,
    show_on_dashboard: account?.show_on_dashboard ?? true,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Please enter a name'); return; }
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>{account ? 'Edit' : 'Create'} Custom {isAd ? 'Ad' : 'Revenue'} Account</CardTitle>
            <button onClick={onClose} className="p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
          </div>
          <CardDescription>{isAd ? 'Track ad spend from any source not covered by our integrations' : 'Track revenue from custom sources like affiliates or direct deals'}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium">Account Name *</label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder={isAd ? 'e.g., TikTok Ads, Reddit Ads' : 'e.g., Amazon Associates, Sponsorships'} className="mt-1" />
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Optional description" className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Currency</label>
                <select value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })}
                  className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background">
                  {['USD','EUR','GBP','NGN','CAD','AUD'].map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">Exchange Rate</label>
                <Input type="number" step="0.0001" value={form.exchange_rate}
                  onChange={e => setForm({ ...form, exchange_rate: parseFloat(e.target.value) || 1 })} className="mt-1" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Tax Rate (%)</label>
              <Input type="number" step="0.01" value={form.tax_rate}
                onChange={e => setForm({ ...form, tax_rate: parseFloat(e.target.value) || 0 })} placeholder="0" className="mt-1" />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.show_on_dashboard}
                onChange={e => setForm({ ...form, show_on_dashboard: e.target.checked })} className="rounded" />
              <span className="text-sm">Show on overview</span>
            </label>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
              <Button type="submit" className="flex-1" disabled={saving}>
                {saving ? 'Saving...' : account ? 'Save Changes' : 'Create Account'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// =============================================================================
// FULL-PAGE: CAMPAIGNS
// =============================================================================
const CampaignsPage = ({ account, accountType, userId, onBack }) => {
  const [campaigns, setCampaigns]       = useState([]);
  const [loading, setLoading]           = useState(true);
  const [showAddForm, setShowAddForm]   = useState(false);
  const [editingId, setEditingId]       = useState(null);
  const [saving, setSaving]             = useState(false);
  const emptyForm = { campaign_id: '', campaign_name: '', status: 'ACTIVE', budget: '', notes: '' };
  const [form, setForm]                 = useState(emptyForm);

  const tableName = accountType === 'ad' ? 'custom_ad_campaigns' : 'custom_revenue_campaigns';
  const acctField = 'account_id';

  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from(tableName).select('*').eq(acctField, account.id).order('campaign_name');
    setCampaigns(data || []);
    setLoading(false);
  }, [account.id, tableName]);

  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

  const resetForm = () => { setForm(emptyForm); setEditingId(null); setShowAddForm(false); };

  const startEdit = (camp) => {
    setForm({ campaign_id: camp.campaign_id || '', campaign_name: camp.campaign_name, status: camp.status || 'ACTIVE', budget: camp.budget ? String(camp.budget) : '', notes: camp.notes || '' });
    setEditingId(camp.id);
    setShowAddForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.campaign_name.trim()) { toast.error('Campaign name is required'); return; }
    setSaving(true);
    const campaignId = form.campaign_id.trim() ||
      `custom_${form.campaign_name.toLowerCase().replace(/[^a-z0-9]+/g, '_')}_${Date.now()}`;
    const payload = {
      campaign_name: form.campaign_name.trim(),
      campaign_id:   campaignId,
      status:        form.status,
      budget:        parseFloat(form.budget) || 0,
      notes:         form.notes.trim(),
      updated_at:    new Date().toISOString(),
    };
    try {
      if (editingId) {
        const { error } = await supabase.from(tableName).update(payload).eq('id', editingId);
        if (error) throw error;
        toast.success('Campaign updated');
      } else {
        const { error } = await supabase.from(tableName).insert({
          user_id: userId, [acctField]: account.id, ...payload, excluded: false,
        });
        if (error) throw error;
        toast.success('Campaign added');
      }
      resetForm();
      loadCampaigns();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  const handleDelete = async (camp) => {
    if (!window.confirm(`Delete "${camp.campaign_name}"? Cannot be undone.`)) return;
    const { error } = await supabase.from(tableName).delete().eq('id', camp.id);
    if (error) { toast.error(error.message); return; }
    toast.success('Deleted');
    loadCampaigns();
  };

  const toggleExcluded = async (camp) => {
    const { error } = await supabase.from(tableName).update({ excluded: !camp.excluded }).eq('id', camp.id);
    if (error) { toast.error(error.message); return; }
    loadCampaigns();
  };

  const statusColors = { ACTIVE: 'bg-emerald-50 text-emerald-700', PAUSED: 'bg-amber-50 text-amber-700', ENDED: 'bg-gray-100 text-gray-500' };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <span className="text-muted-foreground">/</span>
        <h1 className="text-xl font-bold">{account.name}</h1>
        <Badge variant="outline">{accountType === 'ad' ? 'Ad Account' : 'Revenue Account'}</Badge>
        <span className="text-muted-foreground">/</span>
        <span className="font-semibold">Campaigns</span>
      </div>

      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">
            Manage campaigns for this account. Excluded campaigns are filtered from Shopify ROAS calculations.
          </p>
        </div>
        {!showAddForm && (
          <Button onClick={() => { resetForm(); setShowAddForm(true); }}>
            <Plus className="h-4 w-4 mr-2" /> Add Campaign
          </Button>
        )}
      </div>

      {/* Add / Edit form — inline at top when active */}
      {showAddForm && (
        <Card className="border-primary/40 bg-primary/5">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">{editingId ? 'Edit Campaign' : 'New Campaign'}</CardTitle>
              <button onClick={resetForm} className="p-1 hover:bg-muted rounded"><X className="h-4 w-4" /></button>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSave} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Campaign Name *</label>
                  <Input autoFocus value={form.campaign_name} onChange={e => setForm(f => ({ ...f, campaign_name: e.target.value }))} placeholder="e.g. Summer Sale 2025" />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Campaign ID <span className="text-muted-foreground font-normal">(optional)</span></label>
                  <Input value={form.campaign_id} onChange={e => setForm(f => ({ ...f, campaign_id: e.target.value }))} placeholder="Auto-generated if blank" />
                </div>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium mb-1 block">Status</label>
                  <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm">
                    <option value="ACTIVE">Active</option>
                    <option value="PAUSED">Paused</option>
                    <option value="ENDED">Ended</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Budget ({account.currency || 'USD'})</label>
                  <Input type="number" step="0.01" min="0" value={form.budget} onChange={e => setForm(f => ({ ...f, budget: e.target.value }))} placeholder="0.00" />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Notes</label>
                  <Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Optional note" />
                </div>
              </div>
              <div className="flex gap-3 pt-1">
                <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
                <Button type="submit" disabled={saving}>
                  <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : editingId ? 'Update Campaign' : 'Add Campaign'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Campaign list */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : campaigns.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-16 text-center">
            <Tag className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
            <h3 className="font-semibold mb-2">No campaigns yet</h3>
            <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">
              Add campaigns to enable campaign-level filtering in Shopify ROAS calculations.
            </p>
            <Button onClick={() => { resetForm(); setShowAddForm(true); }}>
              <Plus className="h-4 w-4 mr-2" /> Add Your First Campaign
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {/* Column headers */}
          <div className="grid grid-cols-12 gap-4 px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            <div className="col-span-1">Include</div>
            <div className="col-span-4">Campaign</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2">Budget</div>
            <div className="col-span-2">Notes</div>
            <div className="col-span-1 text-right">Actions</div>
          </div>

          {campaigns.map(camp => (
            <Card key={camp.id} className={`transition-all ${camp.excluded ? 'opacity-50 bg-muted/30' : ''}`}>
              <CardContent className="p-4">
                <div className="grid grid-cols-12 gap-4 items-center">
                  {/* Include/exclude toggle */}
                  <div className="col-span-1">
                    <button onClick={() => toggleExcluded(camp)} title={camp.excluded ? 'Click to include' : 'Click to exclude'}
                      className="flex items-center">
                      {camp.excluded
                        ? <ToggleLeft className="h-6 w-6 text-muted-foreground" />
                        : <ToggleRight className="h-6 w-6 text-primary" />}
                    </button>
                  </div>

                  {/* Name + ID */}
                  <div className="col-span-4 min-w-0">
                    <p className={`font-medium truncate ${camp.excluded ? 'line-through text-muted-foreground' : ''}`}>
                      {camp.campaign_name}
                    </p>
                    <p className="text-xs text-muted-foreground font-mono truncate">{camp.campaign_id}</p>
                  </div>

                  {/* Status */}
                  <div className="col-span-2">
                    <span className={`text-xs font-semibold px-2 py-1 rounded-full ${statusColors[camp.status] || statusColors.ENDED}`}>
                      {camp.status || 'ACTIVE'}
                    </span>
                  </div>

                  {/* Budget */}
                  <div className="col-span-2">
                    <p className="text-sm">{camp.budget > 0 ? formatCurrency(camp.budget, account.currency) : <span className="text-muted-foreground">—</span>}</p>
                  </div>

                  {/* Notes */}
                  <div className="col-span-2 min-w-0">
                    <p className="text-sm text-muted-foreground truncate">{camp.notes || <span>—</span>}</p>
                  </div>

                  {/* Actions */}
                  <div className="col-span-1 flex items-center justify-end gap-1">
                    <button onClick={() => startEdit(camp)}
                      className="p-1.5 hover:bg-muted rounded-lg text-muted-foreground hover:text-foreground transition-colors">
                      <Edit2 className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => handleDelete(camp)}
                      className="p-1.5 hover:bg-red-50 rounded-lg text-muted-foreground hover:text-red-500 transition-colors">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {/* Summary footer */}
          <div className="flex items-center justify-between px-4 py-2 text-sm text-muted-foreground">
            <span>{campaigns.filter(c => !c.excluded).length} of {campaigns.length} campaigns included</span>
            {campaigns.some(c => c.excluded) && (
              <span className="text-amber-600">{campaigns.filter(c => c.excluded).length} excluded from ROAS</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// FULL-PAGE: DAILY DATA
// =============================================================================
const DataPage = ({ account, accountType, userId, onBack }) => {
  const isAd = accountType === 'ad';
  const tableName = isAd ? 'custom_ad_daily' : 'custom_revenue_daily';
  const valueField = isAd ? 'spend' : 'revenue';

  const [entries, setEntries]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [editingEntry, setEditingEntry] = useState(null);
  const [showAddForm, setShowAddForm]   = useState(false);
  const [newEntry, setNewEntry]         = useState(null);
  const [saving, setSaving]             = useState(false);

  const monthStr = currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' });

  const loadEntries = useCallback(async () => {
    setLoading(true);
    const start = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).toISOString().split('T')[0];
    const end   = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).toISOString().split('T')[0];
    const { data, error } = await supabase.from(tableName).select('*')
      .eq('account_id', account.id).gte('date', start).lte('date', end).order('date', { ascending: false });
    if (!error) setEntries(data || []);
    setLoading(false);
  }, [account.id, currentMonth, tableName]);

  useEffect(() => { loadEntries(); }, [loadEntries]);

  const blankEntry = () => ({
    date: new Date().toISOString().split('T')[0],
    [valueField]: '', impressions: '', clicks: '',
    ...(isAd ? { conversions: '' } : { page_views: '' }),
    notes: '',
  });

  const handleSaveEntry = async (entry, isNew = false) => {
    setSaving(true);
    const data = {
      user_id: userId, account_id: account.id, date: entry.date,
      [valueField]: parseFloat(entry[valueField]) || 0,
      [`${valueField}_original`]: parseFloat(entry[valueField]) || 0,
      impressions: parseInt(entry.impressions) || 0,
      clicks: parseInt(entry.clicks) || 0,
      notes: entry.notes || '',
      ...(isAd ? { conversions: parseFloat(entry.conversions) || 0 } : { page_views: parseInt(entry.page_views) || 0 }),
    };
    let error;
    if (isNew) { ({ error } = await supabase.from(tableName).insert(data)); }
    else { ({ error } = await supabase.from(tableName).update(data).eq('id', entry.id)); }
    if (error) { toast.error(error.message); }
    else { toast.success(isNew ? 'Entry added' : 'Entry updated'); setEditingEntry(null); setNewEntry(null); loadEntries(); }
    setSaving(false);
  };

  const handleDeleteEntry = async (entry) => {
    if (!window.confirm('Delete this entry?')) return;
    const { error } = await supabase.from(tableName).delete().eq('id', entry.id);
    if (error) { toast.error(error.message); } else { toast.success('Deleted'); loadEntries(); }
  };

  const EntryForm = ({ entry, onSave, onCancel, isNew }) => {
    const [local, setLocal] = useState(entry);
    return (
      <tr className="bg-primary/5 border-t">
        <td className="p-2">
          <Input type="date" value={local.date} onChange={e => setLocal(l => ({ ...l, date: e.target.value }))} className="h-8 text-sm" />
        </td>
        <td className="p-2">
          <Input type="number" step="0.01" value={local[valueField]} onChange={e => setLocal(l => ({ ...l, [valueField]: e.target.value }))} placeholder="0.00" className="h-8 text-sm" />
        </td>
        <td className="p-2 hidden md:table-cell">
          <Input type="number" value={local.impressions} onChange={e => setLocal(l => ({ ...l, impressions: e.target.value }))} placeholder="0" className="h-8 text-sm" />
        </td>
        <td className="p-2 hidden md:table-cell">
          <Input type="number" value={local.clicks} onChange={e => setLocal(l => ({ ...l, clicks: e.target.value }))} placeholder="0" className="h-8 text-sm" />
        </td>
        <td className="p-2 hidden lg:table-cell">
          <Input value={local.notes || ''} onChange={e => setLocal(l => ({ ...l, notes: e.target.value }))} placeholder="Note" className="h-8 text-sm" />
        </td>
        <td className="p-2">
          <div className="flex gap-1 justify-end">
            <Button size="sm" onClick={() => onSave(local, isNew)} disabled={saving}>
              <Save className="h-3.5 w-3.5" />
            </Button>
            <Button size="sm" variant="ghost" onClick={onCancel}><X className="h-3.5 w-3.5" /></Button>
          </div>
        </td>
      </tr>
    );
  };

  const monthTotal = entries.reduce((s, e) => s + parseFloat(e[valueField] || 0), 0);

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <span className="text-muted-foreground">/</span>
        <h1 className="text-xl font-bold">{account.name}</h1>
        <Badge variant="outline">{isAd ? 'Ad Account' : 'Revenue Account'}</Badge>
        <span className="text-muted-foreground">/</span>
        <span className="font-semibold">Daily Data</span>
      </div>

      {/* Month navigation + Add button */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
            className="p-2 hover:bg-muted rounded-lg transition-colors"><ChevronLeft className="h-4 w-4" /></button>
          <span className="font-semibold min-w-[160px] text-center">{monthStr}</span>
          <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
            className="p-2 hover:bg-muted rounded-lg transition-colors"><ChevronRight className="h-4 w-4" /></button>
        </div>
        <div className="flex items-center gap-4">
          {entries.length > 0 && (
            <span className="text-sm text-muted-foreground">
              {monthStr} total: <strong className={isAd ? 'text-red-600' : 'text-green-600'}>{formatCurrency(monthTotal, account.currency)}</strong>
            </span>
          )}
          {!newEntry && (
            <Button onClick={() => setNewEntry(blankEntry())}>
              <Plus className="h-4 w-4 mr-2" /> Add Entry
            </Button>
          )}
        </div>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left p-3 font-semibold">Date</th>
                  <th className="text-right p-3 font-semibold">{isAd ? 'Spend' : 'Revenue'}</th>
                  <th className="text-right p-3 font-semibold hidden md:table-cell">Impressions</th>
                  <th className="text-right p-3 font-semibold hidden md:table-cell">Clicks</th>
                  <th className="text-left p-3 font-semibold hidden lg:table-cell">Notes</th>
                  <th className="text-right p-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {/* Inline new entry row */}
                {newEntry && <EntryForm entry={newEntry} onSave={handleSaveEntry} onCancel={() => setNewEntry(null)} isNew={true} />}

                {loading ? (
                  <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">
                    <RefreshCw className="h-5 w-5 animate-spin mx-auto mb-2" />Loading...
                  </td></tr>
                ) : entries.length === 0 && !newEntry ? (
                  <tr><td colSpan={6} className="p-12 text-center text-muted-foreground">
                    No entries for {monthStr}. Click "Add Entry" to record data.
                  </td></tr>
                ) : entries.map(entry => (
                  editingEntry?.id === entry.id ? (
                    <EntryForm key={entry.id} entry={editingEntry} onSave={handleSaveEntry} onCancel={() => setEditingEntry(null)} isNew={false} />
                  ) : (
                    <tr key={entry.id} className="border-t hover:bg-muted/30 transition-colors">
                      <td className="p-3">{new Date(entry.date + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                      <td className={`p-3 text-right font-semibold ${isAd ? 'text-red-600' : 'text-green-600'}`}>
                        {formatCurrency(entry[valueField] || 0, account.currency)}
                      </td>
                      <td className="p-3 text-right hidden md:table-cell">{formatNumber(entry.impressions || 0)}</td>
                      <td className="p-3 text-right hidden md:table-cell">{formatNumber(entry.clicks || 0)}</td>
                      <td className="p-3 text-muted-foreground hidden lg:table-cell">{entry.notes || '—'}</td>
                      <td className="p-3">
                        <div className="flex justify-end gap-1">
                          <button onClick={() => setEditingEntry({ ...entry })}
                            className="p-1.5 hover:bg-muted rounded-lg text-muted-foreground hover:text-foreground transition-colors">
                            <Edit2 className="h-3.5 w-3.5" />
                          </button>
                          <button onClick={() => handleDeleteEntry(entry)}
                            className="p-1.5 hover:bg-red-50 rounded-lg text-muted-foreground hover:text-red-500 transition-colors">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                ))}

                {/* Month total row */}
                {entries.length > 0 && !loading && (
                  <tr className="border-t bg-muted/30 font-semibold">
                    <td className="p-3">Total</td>
                    <td className={`p-3 text-right ${isAd ? 'text-red-600' : 'text-green-600'}`}>
                      {formatCurrency(monthTotal, account.currency)}
                    </td>
                    <td className="p-3 text-right hidden md:table-cell">
                      {formatNumber(entries.reduce((s, e) => s + (e.impressions || 0), 0))}
                    </td>
                    <td className="p-3 text-right hidden md:table-cell">
                      {formatNumber(entries.reduce((s, e) => s + (e.clicks || 0), 0))}
                    </td>
                    <td className="hidden lg:table-cell" />
                    <td />
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// =============================================================================
// ACCOUNT CARD (overview)
// =============================================================================
const AccountCard = ({ type, account, stats, onEdit, onDelete, onViewData, onViewCampaigns }) => {
  const isAd = type === 'ad';
  const Icon = isAd ? DollarSign : TrendingUp;
  const iconCls = isAd ? 'text-red-500 bg-red-500/10' : 'text-green-500 bg-green-500/10';

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${iconCls}`}><Icon className="h-5 w-5" /></div>
            <div>
              <CardTitle className="text-lg">{account.name}</CardTitle>
              <CardDescription>{account.description || `Custom ${isAd ? 'Ad Spend' : 'Revenue'} Account`}</CardDescription>
            </div>
          </div>
          <div className="flex gap-1">
            <button onClick={() => onEdit(account)} className="p-1.5 hover:bg-muted rounded-lg text-muted-foreground hover:text-foreground transition-colors">
              <Edit2 className="h-4 w-4" />
            </button>
            <button onClick={() => onDelete(account)} className="p-1.5 hover:bg-red-50 rounded-lg text-muted-foreground hover:text-red-500 transition-colors">
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Stats */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-muted-foreground">This Month</p>
            <p className={`text-2xl font-bold ${isAd ? 'text-red-600' : 'text-green-600'}`}>
              {formatCurrency(stats?.thisMonth || 0, account.currency)}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Last Month</p>
            <p className="text-xl font-semibold text-muted-foreground">
              {formatCurrency(stats?.lastMonth || 0, account.currency)}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{stats?.entryCount || 0} entries · {account.currency}</span>
          {account.show_on_dashboard && <Badge variant="outline" className="text-xs">On Overview</Badge>}
        </div>

        {/* Action buttons */}
        <div className="grid grid-cols-2 gap-2 pt-1">
          <Button variant="outline" className="w-full" onClick={() => onViewData(account)}>
            <Calendar className="h-4 w-4 mr-2" /> Daily Data
          </Button>
          <Button variant="outline" className="w-full" onClick={() => onViewCampaigns(account)}>
            <Tag className="h-4 w-4 mr-2" /> Campaigns
            {stats?.campaignCount > 0 && (
              <Badge variant="secondary" className="ml-1.5 text-xs">{stats.campaignCount}</Badge>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// =============================================================================
// MAIN PAGE
// =============================================================================
export default function CustomAccounts() {
  const { user, isPro, isAdmin } = useAuth();
  const [loading, setLoading]               = useState(true);
  const [adAccount, setAdAccount]           = useState(null);
  const [revenueAccount, setRevenueAccount] = useState(null);
  const [adStats, setAdStats]               = useState({});
  const [revenueStats, setRevenueStats]     = useState({});

  // Page navigation state
  // null = overview
  // { page: 'campaigns', account, accountType } = campaigns page
  // { page: 'data', account, accountType }      = daily data page
  const [currentView, setCurrentView] = useState(null);

  // Modals (account create/edit only)
  const [showAdModal, setShowAdModal]         = useState(false);
  const [showRevenueModal, setShowRevenueModal] = useState(false);
  const [editingAd, setEditingAd]             = useState(null);
  const [editingRevenue, setEditingRevenue]   = useState(null);

  const canCreate = isPro || isAdmin;

  const loadAccounts = useCallback(async () => {
    if (!user) return;
    setLoading(true);

    const [{ data: adData }, { data: revData }] = await Promise.all([
      supabase.from('custom_ad_accounts').select('*').eq('user_id', user.id).maybeSingle(),
      supabase.from('custom_revenue_accounts').select('*').eq('user_id', user.id).maybeSingle(),
    ]);

    setAdAccount(adData);
    setRevenueAccount(revData);

    // Load stats in parallel
    await Promise.all([
      adData  ? loadStats('ad',      adData.id)  : Promise.resolve(),
      revData ? loadStats('revenue', revData.id) : Promise.resolve(),
    ]);

    setLoading(false);
  }, [user]);

  const loadStats = async (type, accountId) => {
    const table = type === 'ad' ? 'custom_ad_daily' : 'custom_revenue_daily';
    const campTable = type === 'ad' ? 'custom_ad_campaigns' : 'custom_revenue_campaigns';
    const vf    = type === 'ad' ? 'spend' : 'revenue';
    const now   = new Date();
    const thisStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
    const lastStart = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().split('T')[0];
    const lastEnd   = new Date(now.getFullYear(), now.getMonth(), 0).toISOString().split('T')[0];

    const [{ data: thisM }, { data: lastM }, { count: entryCount }, { count: campCount }] = await Promise.all([
      supabase.from(table).select(vf).eq('account_id', accountId).gte('date', thisStart),
      supabase.from(table).select(vf).eq('account_id', accountId).gte('date', lastStart).lte('date', lastEnd),
      supabase.from(table).select('*', { count: 'exact', head: true }).eq('account_id', accountId),
      supabase.from(campTable).select('*', { count: 'exact', head: true }).eq('account_id', accountId),
    ]);

    const stats = {
      thisMonth:     (thisM || []).reduce((s, r) => s + parseFloat(r[vf] || 0), 0),
      lastMonth:     (lastM || []).reduce((s, r) => s + parseFloat(r[vf] || 0), 0),
      entryCount:    entryCount || 0,
      campaignCount: campCount  || 0,
    };

    if (type === 'ad')      setAdStats(stats);
    else                    setRevenueStats(stats);
  };

  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  const handleSaveAdAccount = async (form) => {
    const data = { user_id: user.id, ...form };
    const { error } = editingAd
      ? await supabase.from('custom_ad_accounts').update(form).eq('id', editingAd.id)
      : await supabase.from('custom_ad_accounts').insert(data);
    if (error) { toast.error(error.message); return; }
    toast.success(editingAd ? 'Account updated' : 'Account created');
    setShowAdModal(false); setEditingAd(null); loadAccounts();
  };

  const handleSaveRevenueAccount = async (form) => {
    const data = { user_id: user.id, ...form };
    const { error } = editingRevenue
      ? await supabase.from('custom_revenue_accounts').update(form).eq('id', editingRevenue.id)
      : await supabase.from('custom_revenue_accounts').insert(data);
    if (error) { toast.error(error.message); return; }
    toast.success(editingRevenue ? 'Account updated' : 'Account created');
    setShowRevenueModal(false); setEditingRevenue(null); loadAccounts();
  };

  const handleDeleteAccount = async (account, type) => {
    if (!window.confirm(`Delete "${account.name}" and all its data? This cannot be undone.`)) return;
    const table = type === 'ad' ? 'custom_ad_accounts' : 'custom_revenue_accounts';
    const { error } = await supabase.from(table).delete().eq('id', account.id);
    if (error) { toast.error(error.message); return; }
    toast.success('Account deleted'); loadAccounts();
  };

  // ── Routing ────────────────────────────────────────────────────────────────
  const goBack = () => { setCurrentView(null); loadAccounts(); };

  if (!canCreate) return <ProUpsell />;

  if (loading) return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );

  // ── Sub-page views ─────────────────────────────────────────────────────────
  if (currentView?.page === 'campaigns') {

  if (loading) return <SkeletonCustomAccounts />;
    return (
      <CampaignsPage
        account={currentView.account}
        accountType={currentView.accountType}
        userId={user.id}
        onBack={goBack}
      />
    );
  }

  if (currentView?.page === 'data') {
    return (
      <DataPage
        account={currentView.account}
        accountType={currentView.accountType}
        userId={user.id}
        onBack={goBack}
      />
    );
  }

  // ── Overview ───────────────────────────────────────────────────────────────
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold">Custom Accounts</h1>
        <p className="text-muted-foreground">Track ad spend and revenue from sources not covered by our integrations</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Ad account */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-red-500" /> Custom Ad Account
            </h2>
            {!adAccount && <Badge variant="outline">0/1 used</Badge>}
          </div>
          {adAccount ? (
            <AccountCard type="ad" account={adAccount} stats={adStats}
              onEdit={acc => { setEditingAd(acc); setShowAdModal(true); }}
              onDelete={acc => handleDeleteAccount(acc, 'ad')}
              onViewData={acc => setCurrentView({ page: 'data', account: acc, accountType: 'ad' })}
              onViewCampaigns={acc => setCurrentView({ page: 'campaigns', account: acc, accountType: 'ad' })}
            />
          ) : (
            <Card className="border-dashed">
              <CardContent className="p-8 text-center">
                <DollarSign className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                <h3 className="font-medium mb-2">No Custom Ad Account</h3>
                <p className="text-sm text-muted-foreground mb-4">Track spend from TikTok, LinkedIn, Reddit, or any other ad platform</p>
                <Button onClick={() => setShowAdModal(true)}><Plus className="h-4 w-4 mr-2" />Create Ad Account</Button>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Revenue account */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-green-500" /> Custom Revenue Account
            </h2>
            {!revenueAccount && <Badge variant="outline">0/1 used</Badge>}
          </div>
          {revenueAccount ? (
            <AccountCard type="revenue" account={revenueAccount} stats={revenueStats}
              onEdit={acc => { setEditingRevenue(acc); setShowRevenueModal(true); }}
              onDelete={acc => handleDeleteAccount(acc, 'revenue')}
              onViewData={acc => setCurrentView({ page: 'data', account: acc, accountType: 'revenue' })}
              onViewCampaigns={acc => setCurrentView({ page: 'campaigns', account: acc, accountType: 'revenue' })}
            />
          ) : (
            <Card className="border-dashed">
              <CardContent className="p-8 text-center">
                <TrendingUp className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                <h3 className="font-medium mb-2">No Custom Revenue Account</h3>
                <p className="text-sm text-muted-foreground mb-4">Track revenue from affiliates, sponsorships, or direct ad sales</p>
                <Button onClick={() => setShowRevenueModal(true)}><Plus className="h-4 w-4 mr-2" />Create Revenue Account</Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Account create/edit modals */}
      {showAdModal && (
        <AccountModal type="ad" account={editingAd} onSave={handleSaveAdAccount}
          onClose={() => { setShowAdModal(false); setEditingAd(null); }} />
      )}
      {showRevenueModal && (
        <AccountModal type="revenue" account={editingRevenue} onSave={handleSaveRevenueAccount}
          onClose={() => { setShowRevenueModal(false); setEditingRevenue(null); }} />
      )}
    </div>
  );
}
